<template>
  <div class="repository-collaborators-form-scope__caption">Share scope</div>
  <UIRadiobox v-model="shareScope" :value="ShareScope.WHOLE_REPO" class="repository-collaborators-form-scope__radio">
    <RectangleStackIcon aria-hidden="true" />
    <div>
      <p>Whole Repo</p>
      <p class="repository-collaborators-form-scope__radio-description">Access to the entire repository</p>

    </div>
  </UIRadiobox>
  <UIRadiobox
    v-model="shareScope"
    :value="ShareScope.SELECTED_ACCOUNTS"
    :disabled="!isPartialShareEnabled"
    class="repository-collaborators-form-scope__radio"
  >
    <UsersIcon aria-hidden="true" />
    <div>
      <p>Selected Accounts</p>
      <p class="repository-collaborators-form-scope__radio-description">Choose specific accounts to share</p>
    </div>
  </UIRadiobox>
  <div
    v-if="shareScope === ShareScope.SELECTED_ACCOUNTS"
    class="repository-collaborators-form-scope__selected-accounts"
    @click="handleShareAssets"
  >
    <UsersIcon aria-hidden="true" />
    <div class="repository-collaborators-form-scope__selected-accounts-content">
      <p class="repository-collaborators-form-scope__selected-accounts-title">Selected Accounts</p>
      <p class="repository-collaborators-form-scope__selected-accounts-description">
        {{ modelValue.length ? `${modelValue.length} account${modelValue.length > 1 ? 's' : ''} selected` : 'No selected accounts yet' }}
      </p>
    </div>
    <ChevronRightIcon aria-hidden="true" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, watch } from 'vue'

import { ShareScope } from '../utils/enums'

import { ACCESS_TYPE_READONLY } from '@/const'

import { useModalsStore } from '@/store/modals'

import CollaboratorsFormScopeAccounts from './CollaboratorsFormScopeAccounts.vue'
import { UIRadiobox } from '@ui/controllers'
import { ChevronRightIcon, RectangleStackIcon, UsersIcon } from '@heroicons/vue/24/outline'

type Props = {
  repositoryId: string
  accessType?: number
}

const props = defineProps<Props>()

const modelValue = defineModel<string[]>({ required: true })
const shareScope = defineModel<ShareScope>('share-scope', { required: true })

const modalsStore = useModalsStore()

const isPartialShareEnabled = computed(() => props.accessType === ACCESS_TYPE_READONLY)

const handleClearSelection = () => {
  modelValue.value = []
}

const handleShareAssets = () => {
  const modal = modalsStore.init('collaborators-form-scope-assets', markRaw(CollaboratorsFormScopeAccounts))
  modal?.open(modalsStore.getZIndex(), {
    assetIds: modelValue.value,
    repositoryId: props.repositoryId,
    onDone: (value: string[]) => {
      modelValue.value = value
    },
  })
}

watch(shareScope, value => {
  if (value === ShareScope.SELECTED_ACCOUNTS) {
    handleShareAssets()
  } else {
    handleClearSelection()
  }
})

watch(isPartialShareEnabled, value => {
  if (value) return
  shareScope.value = ShareScope.WHOLE_REPO
})
</script>

<style>
.repository-collaborators-form-scope {

  &__caption {
    @apply -mb-1.5;
    @apply col-span-full;
    @apply text-xs font-medium;
  }

  &__radio,
  &__selected-accounts {
    @apply p-2.5;
    @apply border;
    @apply rounded-md;
    @apply cursor-pointer;

    svg {
      @apply shrink-0;
      @apply size-6 last:size-4;
      @apply text-primary dark:text-primary-selected last:text-light-text dark:last:text-gray-500;
    }
  }

  &__radio {
    @apply sm:col-span-6;
    @apply border-light-border hover:border-common-border dark:hover:border-gray-500;

    &:has(.ui-radiobox__control:checked) {
      @apply border-gray-300 dark:border-gray-600;
      @apply bg-gray-50 dark:bg-gray-800;
    }

    &:has(.ui-radiobox__control:disabled) {
      @apply cursor-not-allowed;
    }

    .ui-radiobox__control {
      @apply mt-0.5;
    }

    .ui-radiobox__label {
      @apply flex items-start gap-2;

      svg {
        @apply -mt-0.5;
      }
    }
  }

  &__selected-accounts {
    @apply flex items-center gap-2;
    @apply col-span-full;
    @apply bg-indigo-50/25;
    @apply border-indigo-200/50 hover:border-indigo-200/75;
    @apply dark:bg-indigo-950/25 dark:border-gray-600 dark:hover:border-gray-500;
  }

  &__selected-accounts-content {
    @apply flex-auto;
  }

  &__selected-accounts-title {
    @apply text-sm font-medium;
  }

  &__radio-description,
  &__selected-accounts-description {
    @apply text-xs text-light-text;
    @apply font-normal leading-tight;
  }

  &__radio-description {
    @apply mt-1;
  }
}
</style>
